import { environment } from 'environments/environment';

/** Ashcorp Control Center — platform administration. cc.ashcorp.xyz/co */
export function isAshcorpCcHost(): boolean {
    if (typeof window === 'undefined') {
        return false;
    }
    const host = (window.location.hostname || '').toLowerCase();
    return host === 'cc.ashcorp.co' || host === 'cc.ashcorp.xyz';
}

/**
 * Ashcorp Service Store — public catalog for platform products (Outbox, modules, domains).
 * Purchases here can trigger Docker / workspace provisioning. services.ashcorp.xyz/co
 */
export function isAshcorpServiceStoreHost(): boolean {
    if (typeof window === 'undefined') {
        return false;
    }
    const host = (window.location.hostname || '').toLowerCase();
    return host === 'services.ashcorp.co' || host === 'services.ashcorp.xyz';
}

/** @deprecated Use isAshcorpServiceStoreHost */
export function isAshcorpShopHost(): boolean {
    return isAshcorpServiceStoreHost();
}

/** @deprecated Use isAshcorpServiceStoreHost */
export function isStoreDemoHost(): boolean {
    return isAshcorpServiceStoreHost();
}

/**
 * Ashcorp Back Office — staff approve orders, provision infra, templates.
 * bo.ashcorp.xyz/co
 */
export function isAshcorpBoHost(): boolean {
    if (typeof window === 'undefined') {
        return false;
    }
    const host = (window.location.hostname || '').toLowerCase();
    return host === 'bo.ashcorp.co' || host === 'bo.ashcorp.xyz';
}

/** @deprecated Use isAshcorpBoHost */
export function isServicesBoHost(): boolean {
    return isAshcorpBoHost();
}

/** @deprecated Use isAshcorpBoHost */
export function isServicesHost(): boolean {
    return isAshcorpBoHost();
}

/** Default Ashcorp zone tenant store, e.g. hotel-mu-store.ashcorp.xyz */
export function isTenantStoreAshcorpHost(): boolean {
    if (typeof window === 'undefined') {
        return false;
    }
    const host = (window.location.hostname || '').toLowerCase();
    return /^[a-z0-9-]+-store\.ashcorp\.(co|xyz)$/.test(host);
}

/** Custom tenant store subdomain, e.g. store.hotel.mu */
export function isCustomTenantStoreHost(): boolean {
    if (typeof window === 'undefined') {
        return false;
    }
    const host = (window.location.hostname || '').toLowerCase();
    if (isAshcorpServiceStoreHost() || isTenantStoreAshcorpHost()) {
        return false;
    }
    const parts = host.split('.');
    return parts.length >= 3 && parts[0] === 'store';
}

/**
 * Tenant store storefront — products, services, rentals (API commerce only, no infra).
 */
export function isTenantStoreHost(): boolean {
    if (isAshcorpServiceStoreHost() || isAshcorpBoHost() || isAshcorpCcHost()) {
        return false;
    }
    if (isTenantStoreAshcorpHost() || isCustomTenantStoreHost()) {
        return true;
    }
    return readSurface() === 'tenant-store';
}

/** Custom tenant panel / Outbox web, e.g. cc.hotel.mu */
export function isCustomTenantPanelHost(): boolean {
    if (typeof window === 'undefined') {
        return false;
    }
    const host = (window.location.hostname || '').toLowerCase();
    if (isAshcorpCcHost()) {
        return false;
    }
    const parts = host.split('.');
    return parts.length >= 3 && (parts[0] === 'cc' || parts[0] === 'web');
}

function readSurface(): string | undefined {
    if (typeof window === 'undefined') {
        return undefined;
    }
    const w = window as Window & { __ASHCORP_SURFACE__?: string };
    if (w.__ASHCORP_SURFACE__) {
        return w.__ASHCORP_SURFACE__;
    }
    return (environment as { surface?: string }).surface;
}

/** Any public storefront (Ashcorp Service Store or tenant store). */
export function isExternalStoreHost(): boolean {
    return isAshcorpServiceStoreHost() || isTenantStoreHost();
}

export function isStoreHost(): boolean {
    return isExternalStoreHost();
}

/** Tenant Outbox panel — orders, POS, modules (not a public shop). */
export function isTenantAppHost(): boolean {
    if (typeof window === 'undefined') {
        return false;
    }
    if (isAshcorpBoHost() || isAshcorpCcHost() || isExternalStoreHost()) {
        return false;
    }
    if (isCustomTenantPanelHost() || readSurface() === 'tenant') {
        return true;
    }
    const host = (window.location.hostname || '').toLowerCase();
    return (
        host === 'web.ashcorp.co'
        || host === 'web.ashcorp.xyz'
        || (host.endsWith('.ashcorp.xyz') && !isTenantStoreAshcorpHost() && !isAshcorpServiceStoreHost() && !isAshcorpBoHost() && !isAshcorpCcHost())
        || (host.endsWith('.ashcorp.co') && !isAshcorpServiceStoreHost() && !isAshcorpBoHost())
    );
}
